import { jwtDecode } from 'jwt-decode';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clearDashboardCache } from '../forge/hooks/dashboardCache';
import LoginForm from '../pages/login/components/LoginForm';
import {
  ApiError,
  ApiService,
  CdnAuthError,
  clearStoresCache,
  setNoAuthCallback,
} from '../services/api';
import { checkPermission, transformPermissions } from '../utils/permissions';
import { useNotificationContext } from './NotificationContext';

interface AuthContextValues {
  token: string;
  legacyToken: string;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  user: { id: string; name: string; email?: string } | null;
  hasInternalAccess: boolean;
  hasRemoteDataCollectionAccess: boolean;
  hasPogRulesReadAccess: boolean;
  hasPogRulesWriteAccess: boolean;
  hasCameraDetailsAccess: boolean;
  canUseNonAdminPreview: boolean;
  isNonAdminPreview: boolean;
  enterNonAdminPreview: () => void;
  exitNonAdminPreview: () => void;
}

export const AuthContext = createContext<AuthContextValues>({
  token: '',
  legacyToken: '',
  login: () => Promise.reject(new Error('Not implemented')),
  logout: () => {},
  user: null,
  hasInternalAccess: false,
  hasRemoteDataCollectionAccess: false,
  hasPogRulesReadAccess: false,
  hasPogRulesWriteAccess: false,
  hasCameraDetailsAccess: false,
  canUseNonAdminPreview: false,
  isNonAdminPreview: false,
  enterNonAdminPreview: () => {},
  exitNonAdminPreview: () => {},
});

type AuthContextProviderProps = PropsWithChildren<{}>;

type AuthPayload = {
  exp: number;
  id: string;
  name: string;
  email?: string;
  new_token: string;
  permissions: string[];
};

type StoredAuthState = {
  token: string;
  legacyToken: string;
  tokenExp: number | null;
};

const NON_ADMIN_PREVIEW_KEY = 'forgeNonAdminPreview';

const clearAuthStorage = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('legacyToken');
  localStorage.removeItem('tokenExp');
  localStorage.removeItem(NON_ADMIN_PREVIEW_KEY);
};

const isAuthPayloadValid = (authPayload: unknown): authPayload is AuthPayload => {
  if (typeof authPayload !== 'object' || authPayload === null) {
    return false;
  }
  const payload = authPayload as Partial<AuthPayload>;
  if (typeof payload.exp !== 'number') {
    return false;
  }
  if (
    typeof payload.id !== 'string' ||
    typeof payload.name !== 'string' ||
    (payload.email !== undefined && typeof payload.email !== 'string') ||
    typeof payload.new_token !== 'string' ||
    !Array.isArray(payload.permissions) ||
    !payload.permissions.every((permission) => typeof permission === 'string')
  ) {
    return false;
  }
  const now = Date.now().valueOf() / 1000;
  return payload.exp >= now;
};

const getStoredAuthState = (): StoredAuthState => {
  const token = localStorage.getItem('token') || '';
  const legacyToken = localStorage.getItem('legacyToken') || '';
  const rawTokenExp = localStorage.getItem('tokenExp');
  const tokenExp = rawTokenExp ? Number(rawTokenExp) : null;

  if (!token || !legacyToken || !tokenExp || !Number.isFinite(tokenExp)) {
    clearAuthStorage();
    return { token: '', legacyToken: '', tokenExp: null };
  }

  if (tokenExp * 1000 <= Date.now()) {
    clearAuthStorage();
    return { token: '', legacyToken: '', tokenExp: null };
  }

  try {
    if (!isAuthPayloadValid(jwtDecode(legacyToken))) {
      clearAuthStorage();
      return { token: '', legacyToken: '', tokenExp: null };
    }
  } catch {
    clearAuthStorage();
    return { token: '', legacyToken: '', tokenExp: null };
  }

  return { token, legacyToken, tokenExp };
};

const isLocalDevelopmentHost = () => {
  if (process.env.NODE_ENV !== 'development') {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const consumeLocalDevelopmentLoginToken = () => {
  if (!isLocalDevelopmentHost()) {
    return null;
  }
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  const token = params.get('focal_dev_login_token');
  if (!token) {
    return null;
  }

  params.delete('focal_dev_login_token');
  const nextHash = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ''}`
  );
  return token;
};

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({ children }) => {
  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const [initialAuthState] = useState(getStoredAuthState);
  const [tokenExp, setTokenExp] = useState<number | null>(initialAuthState.tokenExp);
  const [token, setToken] = useState(initialAuthState.token);
  const [legacyToken, setLegacyToken] = useState(initialAuthState.legacyToken);
  const [isNonAdminPreview, setIsNonAdminPreview] = useState(
    () => localStorage.getItem(NON_ADMIN_PREVIEW_KEY) === 'true'
  );

  const user = useMemo(() => {
    try {
      if (!legacyToken) {
        return null;
      }

      const authPayload = jwtDecode(legacyToken);
      if (!isAuthPayloadValid(authPayload)) {
        return null;
      }

      return {
        id: authPayload.id,
        name: authPayload.name,
        ...(authPayload.email !== undefined ? { email: authPayload.email } : {}),
        permissions: authPayload.permissions,
      };
    } catch {
      return null;
    }
  }, [legacyToken]);

  // A tab can change hands without a logout: `adoptStoredAuth` picks up whatever
  // token localStorage holds, which is a different user's once they sign in in
  // another tab. Dashboards cached under the previous identity must not be
  // served to the new one.
  const cachedForUserIdRef = useRef<string | null>(user?.id ?? null);
  useEffect(() => {
    const userId = user?.id ?? null;
    if (cachedForUserIdRef.current === userId) return;
    cachedForUserIdRef.current = userId;
    clearDashboardCache();
  }, [user?.id]);

  const persistToken = useCallback((token: string) => {
    try {
      const authPayload = jwtDecode(token);

      if (!isAuthPayloadValid(authPayload)) {
        return;
      }

      setToken(authPayload.new_token);
      localStorage.setItem('token', authPayload.new_token);

      setTokenExp(authPayload.exp);
      localStorage.setItem('tokenExp', authPayload.exp.toString());

      setLegacyToken(token);
      localStorage.setItem('legacyToken', token);
    } catch {}
  }, []);

  // Identifies the session that owns an in-flight request. Every logout and every
  // fresh login bumps it, so a response belonging to an abandoned session can be
  // recognised and dropped instead of resurrecting or replacing the current one.
  const sessionIdRef = useRef(0);
  const extendAbortRef = useRef<AbortController | null>(null);
  const isExtendingRef = useRef(false);
  const cdnAuthFailureReportedRef = useRef(false);

  const startNewSession = useCallback(() => {
    sessionIdRef.current += 1;
    extendAbortRef.current?.abort();
    extendAbortRef.current = null;
    isExtendingRef.current = false;
    // The next session gets its own chance to report a CDN failure; otherwise the
    // second user on this tab would hit the same broken images in silence.
    cdnAuthFailureReportedRef.current = false;
    return sessionIdRef.current;
  }, []);

  const loginToCdn = useCallback(
    async (token: string) => {
      try {
        await ApiService.loginCdn(token);
        cdnAuthFailureReportedRef.current = false;
      } catch (err) {
        // Without a CDN session every image request is rejected, which otherwise
        // shows up only as silently broken images. Report it once per failing
        // session rather than on each 30-minute refresh.
        console.error('Failed to authenticate with the image CDN', err);
        // A single region failing while the others authenticated is logged but not
        // announced: the user's own region most likely still works, and a toast on
        // every partial blip would train people to ignore it.
        const everyCdnFailed = !(err instanceof CdnAuthError) || err.isTotalFailure;
        if (everyCdnFailed && !cdnAuthFailureReportedRef.current) {
          cdnAuthFailureReportedRef.current = true;
          showErrorToast('Could not sign in to the image CDN, so images may not load.');
        }
      }
    },
    [showErrorToast]
  );

  useEffect(() => {
    const developmentLoginToken = consumeLocalDevelopmentLoginToken();
    if (!developmentLoginToken) {
      return;
    }
    startNewSession();
    persistToken(developmentLoginToken);
    loginToCdn(developmentLoginToken);
  }, [persistToken, loginToCdn, startNewSession]);

  useEffect(() => {
    if (legacyToken) {
      loginToCdn(legacyToken);

      const intervalId = setInterval(
        () => {
          loginToCdn(legacyToken);
        },
        30 * 60 * 1000
      ); // 30 minutes

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [legacyToken, loginToCdn]);

  const login = useCallback(
    async (username: string, password: string) => {
      toggleLoading(true);
      try {
        const token = await ApiService.login(username, password);
        startNewSession();
        persistToken(token);
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Connection error');
      }
      toggleLoading(false);
    },
    [showErrorToast, toggleLoading, persistToken, startNewSession]
  );

  const logout = useCallback(
    (message = '') => {
      startNewSession();
      setToken('');
      setLegacyToken('');
      setTokenExp(null);
      clearAuthStorage();
      clearStoresCache();
      // The next user of this tab must not be seeded with this one's dashboards.
      clearDashboardCache();
      if (message) {
        showErrorToast(message);
      } else {
        showSuccessToast('Logged out successfully');
      }
    },
    [showErrorToast, showSuccessToast, startNewSession]
  );

  const adoptStoredAuth = useCallback((currentExp: number | null): number | null => {
    const stored = getStoredAuthState();
    if (!stored.token || !stored.legacyToken || !stored.tokenExp) {
      return null;
    }
    if (currentExp !== null && stored.tokenExp <= currentExp) {
      return null;
    }
    setToken(stored.token);
    setLegacyToken(stored.legacyToken);
    setTokenExp(stored.tokenExp);
    return stored.tokenExp;
  }, []);

  const extendToken = useCallback(async () => {
    if (isExtendingRef.current) {
      return;
    }
    isExtendingRef.current = true;
    const sessionId = sessionIdRef.current;
    const abortController = new AbortController();
    extendAbortRef.current = abortController;
    // A logout or a newer login moves the session on. Anything this request has to
    // say after that point is about a session that no longer exists.
    const ownsSession = () => sessionId === sessionIdRef.current;
    try {
      const token = await ApiService.extendToken(legacyToken, abortController.signal);

      if (!ownsSession()) {
        return;
      }
      persistToken(token);
    } catch (err) {
      if (!ownsSession()) {
        return;
      }
      if (err instanceof ApiError && err.status === 401) {
        if (!adoptStoredAuth(tokenExp)) {
          logout('Your session cannot be extended, please log in again');
        }
        return;
      }
      console.error('Failed to extend session token', err);
    } finally {
      // A newer session has already reset these; leave its state alone.
      if (ownsSession()) {
        isExtendingRef.current = false;
        extendAbortRef.current = null;
      }
    }
  }, [legacyToken, tokenExp, persistToken, adoptStoredAuth, logout]);

  useEffect(() => {
    if (!tokenExp) {
      return;
    }

    const checkTokenExpiration = () => {
      const effectiveExp = adoptStoredAuth(tokenExp) ?? tokenExp;

      const diff = effectiveExp * 1000 - new Date().getTime();

      if (diff < 0) {
        logout('Session expired, please log in again');
        return;
      }

      // Must stay under 30 minutes: backend counts earlier extension
      // attempts against a separate limit instead of extending
      if (diff < 25 * 60 * 1000) {
        extendToken();
      }
    };

    const checkIfVisible = () => {
      if (document.visibilityState === 'visible') {
        checkTokenExpiration();
      }
    };

    checkTokenExpiration();

    const intervalId = setInterval(checkTokenExpiration, 60 * 1000);
    document.addEventListener('visibilitychange', checkIfVisible);
    window.addEventListener('focus', checkIfVisible);
    window.addEventListener('online', checkIfVisible);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', checkIfVisible);
      window.removeEventListener('focus', checkIfVisible);
      window.removeEventListener('online', checkIfVisible);
    };
  }, [tokenExp, extendToken, logout, adoptStoredAuth]);

  useEffect(() => {
    setNoAuthCallback(() => {
      if (!adoptStoredAuth(tokenExp)) {
        logout('Session expired, please log in again');
      }
    });
  }, [logout, adoptStoredAuth, tokenExp]);

  const permissions = useMemo(() => {
    return transformPermissions(user?.permissions ?? []);
  }, [user]);

  const canUseNonAdminPreview = useMemo(() => {
    return checkPermission('control:*', permissions);
  }, [permissions]);

  const hasInternalAccess = canUseNonAdminPreview && !isNonAdminPreview;
  const hasRemoteDataCollectionAccess = useMemo(
    () =>
      checkPermission('forge:master-label:r', permissions) ||
      checkPermission('forge:master-label:w', permissions),
    [permissions]
  );

  // POG rules sit outside the `control:*` umbrella deliberately: the permission is `pog-rules:*`,
  // so a blanket Control grant does not reach them and the page has to be granted on its own.
  // Write implies read, since the API's read endpoints back the page the write controls live on.
  const hasPogRulesWriteAccess = useMemo(
    () => checkPermission('pog-rules:w', permissions),
    [permissions]
  );
  const hasPogRulesReadAccess = useMemo(
    () => checkPermission('pog-rules:r', permissions) || hasPogRulesWriteAccess,
    [permissions, hasPogRulesWriteAccess]
  );

  // Admin-only camera details (install data + open field-tool tasks). Hidden in non-admin preview.
  const hasCameraDetailsAccess = useMemo(
    () => checkPermission('control:camera-details', permissions) && !isNonAdminPreview,
    [permissions, isNonAdminPreview]
  );

  useEffect(() => {
    if (!canUseNonAdminPreview && isNonAdminPreview) {
      setIsNonAdminPreview(false);
      localStorage.removeItem(NON_ADMIN_PREVIEW_KEY);
    }
  }, [canUseNonAdminPreview, isNonAdminPreview]);

  const enterNonAdminPreview = useCallback(() => {
    if (!canUseNonAdminPreview) return;
    setIsNonAdminPreview(true);
    localStorage.setItem(NON_ADMIN_PREVIEW_KEY, 'true');
  }, [canUseNonAdminPreview]);

  const exitNonAdminPreview = useCallback(() => {
    setIsNonAdminPreview(false);
    localStorage.removeItem(NON_ADMIN_PREVIEW_KEY);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        legacyToken,
        login,
        logout,
        user,
        hasInternalAccess,
        hasRemoteDataCollectionAccess,
        hasPogRulesReadAccess,
        hasPogRulesWriteAccess,
        hasCameraDetailsAccess,
        canUseNonAdminPreview,
        isNonAdminPreview,
        enterNonAdminPreview,
        exitNonAdminPreview,
      }}
    >
      {token ? children : <LoginForm onLogin={login} />}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextValues => useContext(AuthContext);
